import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, Check, LogIn, Users } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/Header';
import { toast } from 'sonner';

const JoinOrganization = () => {
  const { inviteToken } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/organizations/invite/${inviteToken}/info`);
        setInvite(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invitation invalide ou expiree');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [inviteToken]);

  const handleJoin = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour rejoindre l\'organisation');
      navigate(`/login?redirect=/join/${inviteToken}`);
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(`${API}/organizations/join/${inviteToken}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setJoined(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="join-org-page">
      <Header />
      <div className="max-w-md mx-auto px-4 py-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="card-brutal text-center py-12">
            <div className="bg-danger/10 w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-danger" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2 text-danger">Invitation invalide</h2>
            <p className="text-charcoal/60 mb-6">{error}</p>
            <Link to="/" className="btn-primary">Retour a l'accueil</Link>
          </div>
        ) : joined ? (
          <div className="card-brutal text-center py-12" data-testid="join-success">
            <div className="bg-green-100 w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2">Vous avez rejoint l'organisation !</h2>
            <p className="text-charcoal/60 mb-6">{invite?.org_name}</p>
            <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard-btn">Aller au Dashboard</Link>
          </div>
        ) : (
          <div className="card-brutal text-center py-12" data-testid="join-invite">
            <div className="bg-primary/20 w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-10 h-10 text-charcoal" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2">Rejoindre une organisation</h2>
            <p className="text-2xl font-heading font-bold text-charcoal mb-1">{invite?.org_name}</p>
            <p className="text-sm text-charcoal/60 mb-6">
              Role : <span className="font-bold">{invite?.role === 'admin' ? 'Administrateur' : 'Membre'}</span>
            </p>
            {user ? (
              <button
                onClick={handleJoin}
                disabled={joining}
                className="btn-primary w-full max-w-xs mx-auto flex items-center justify-center gap-2"
                data-testid="join-org-btn"
              >
                {joining ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-charcoal border-t-transparent" />
                ) : (
                  <><Users className="w-5 h-5" /> Rejoindre</>
                )}
              </button>
            ) : (
              <div>
                <p className="text-sm text-charcoal/60 mb-4">Connectez-vous pour rejoindre cette organisation</p>
                <Link to={`/login?redirect=/join/${inviteToken}`} className="btn-primary inline-flex items-center gap-2" data-testid="login-to-join-btn">
                  <LogIn className="w-5 h-5" /> Se connecter
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinOrganization;
